// src/utils/columnRegistry.ts

import React from "react";
import { ColDef } from "ag-grid-community";
import {
  nameRenderer,
  phoneRenderer,
  sourceRenderer,
  statusRenderer,
  labelRenderer,
  lastActivityRenderer,
  dateTimeRenderer,
  createdByRenderer,
  assignedToRenderer,
  saleValueRenderer,
  campaignNameRenderer,
  formNameRenderer,
  leadIdRenderer,
} from "../views/leads/leadsGrid/leads-renderers";
import logger from "./logger";

// Column ID constants to avoid typos
export const COLUMN_IDS = {
  LEAD_ID: "leadId",
  NAME: "name",
  PHONE: "phone",
  SOURCE: "integration",
  STATUS: "status",
  LABEL: "label",
  CREATED_AT: "createdAt",
  CREATED_BY: "createdBy",
  LAST_ACTIVITY: "lastActivityLabel",
  ASSIGNED_TO: "assignedTo",
  SALE_VALUE: "saleValue",
  FB_CAMPAIGN: "campaign_name",
  FB_FORM: "formName",
};

// Base column definition interface with proper typing
export interface BaseColumnDefinition {
  headerName: string;
  field: string;
  colId: string;
  width?: number;
  minWidth?: number;
  sortable?: boolean;
  cellRenderer?: any;
  suppressNavigable?: boolean;
  headerCheckboxSelection?: boolean;
  checkboxSelection?: boolean;
  cellRendererParams?: any;
  type?: string;
  hasDistributionChart?: boolean;
  // NEW: Column filter configuration
  allowColumnFilter?: boolean;
  columnFilterType?: 'text' | 'number' | 'date' | 'selection' | 'multi-selection';
  columnFilterOptions?: Array<{ label: string; value: string }>;
}

// The registry object
export const ColumnRegistry: Record<string, BaseColumnDefinition> = {
  [COLUMN_IDS.LEAD_ID]: {
    headerName: "Lead ID",
    field: "leadId",
    colId: COLUMN_IDS.LEAD_ID,
    width: 120,
    sortable: false,
    cellRenderer: leadIdRenderer,
    allowColumnFilter: true,
    columnFilterType: 'text',
  },
  [COLUMN_IDS.NAME]: {
    headerName: "Client Name",
    field: "name",
    colId: COLUMN_IDS.NAME,
    width: 150,
    sortable: false,
    cellRenderer: nameRenderer,
    checkboxSelection: true,
    allowColumnFilter: true,
    columnFilterType: 'text',
  },
  [COLUMN_IDS.PHONE]: {
    headerName: "Phone Number",
    field: "phone",
    colId: COLUMN_IDS.PHONE,
    width: 500,
    sortable: false,
    cellRenderer: phoneRenderer,
    suppressNavigable: true,
  },
  [COLUMN_IDS.SOURCE]: {
    headerName: "Source",
    field: "integration",
    colId: COLUMN_IDS.SOURCE,
    width: 180,
    sortable: false,
    cellRenderer: sourceRenderer,
  },
  [COLUMN_IDS.STATUS]: {
    headerName: "Status",
    field: "status",
    colId: COLUMN_IDS.STATUS,
    width: 150,
    sortable: false,
    cellRenderer: statusRenderer,
    // Add this:
    cellRendererParams: {
      // preferences will be filled in during column processing
      // We just need to establish the property here
      preferences: null
    },
      hasDistributionChart: true
  },
  [COLUMN_IDS.LABEL]: {
    headerName: "Label",
    field: "label",
    colId: COLUMN_IDS.LABEL,
    width: 220,
    sortable: false,
    cellRenderer: labelRenderer,
    // Add this:
    cellRendererParams: {
      // preferences will be filled in during column processing
      preferences: null
    },
    hasDistributionChart: true
  },
  [COLUMN_IDS.CREATED_AT]: {
    headerName: "Created At",
    field: "createdAt",
    colId: COLUMN_IDS.CREATED_AT,
    width: 140,
    minWidth: 110,
    sortable: false,
    cellRenderer: dateTimeRenderer,
    hasDistributionChart: true,
    headerClass: "ag-header-text-overflow",
    allowColumnFilter: true,
    columnFilterType: 'date',
  } as BaseColumnDefinition,
  [COLUMN_IDS.CREATED_BY]: {
    headerName: "Created By",
    field: "createdBy",
    colId: COLUMN_IDS.CREATED_BY,
    width: 180,
    sortable: false,
    cellRenderer: createdByRenderer,
  },
  [COLUMN_IDS.LAST_ACTIVITY]: {
    headerName: "Last Activity",
    field: "lastActivityLabel",
    colId: COLUMN_IDS.LAST_ACTIVITY,
    width: 250,
    sortable: false,
    cellRenderer: lastActivityRenderer,
  },
  [COLUMN_IDS.ASSIGNED_TO]: {
    headerName: "Assigned To",
    field: "assignedTo",
    colId: COLUMN_IDS.ASSIGNED_TO,
    width: 200,
    sortable: false,
    cellRenderer: assignedToRenderer,
     hasDistributionChart: true  
  },
  [COLUMN_IDS.SALE_VALUE]: {
    headerName: "Sale Value",
    field: "saleValue",
    colId: COLUMN_IDS.SALE_VALUE,
    width: 150,
    sortable: false,
    cellRenderer: saleValueRenderer,
    allowColumnFilter: true,
    columnFilterType: 'number',
  },
  [COLUMN_IDS.FB_CAMPAIGN]: {
    headerName: "FB Campaign",
    field: "extraDetails.campaign_name",
    colId: COLUMN_IDS.FB_CAMPAIGN,
    width: 180,
    sortable: false,
    cellRenderer: campaignNameRenderer,
  },
  [COLUMN_IDS.FB_FORM]: {
    headerName: "FB Lead Form",
    field: "extraDetails.formName",
    colId: COLUMN_IDS.FB_FORM,
    width: 180,
    sortable: false,
    cellRenderer: formNameRenderer,
  },
};

// Function to get the default column order
export const getDefaultColumnOrder = (): string[] => [
  COLUMN_IDS.LEAD_ID,
  COLUMN_IDS.NAME,
  COLUMN_IDS.PHONE,
  COLUMN_IDS.SALE_VALUE,
  COLUMN_IDS.STATUS,
  COLUMN_IDS.CREATED_AT,
  COLUMN_IDS.SOURCE,
  COLUMN_IDS.LAST_ACTIVITY,
  COLUMN_IDS.CREATED_BY,
  COLUMN_IDS.ASSIGNED_TO,
  COLUMN_IDS.LABEL,
  // FB-specific columns are not included by default but are available in the registry
];

// Function to get read-only columns
export const getReadOnlyColumns = (): string[] => [
  COLUMN_IDS.NAME,
  COLUMN_IDS.PHONE,
  COLUMN_IDS.SOURCE,
  COLUMN_IDS.STATUS,
  COLUMN_IDS.LABEL,
  COLUMN_IDS.LAST_ACTIVITY,

  // Note: LEAD_ID, CREATED_AT and CREATED_BY are not read-only
];


// Function to get all available column definitions as array
export const getAllColumnDefs = (): ColDef[] => {
  return Object.values(ColumnRegistry);
};



// Function to get column definitions for a specific set of column IDs
export const getColumnDefsForIds = (columnIds: string[]): ColDef[] => {
  return columnIds
    .map(id => ColumnRegistry[id])
    .filter(Boolean); // Filter out any undefined entries
};

// Helper function to create custom field columns
export const createCustomFieldColumnDef = (
  form: any, 
  columnWidths: Record<string, number> = {}
): ColDef => {
  let cellRenderer;
  
  // Use editable renderer for custom fields, but keep special renderers for specific types
  if (form.type === "location") {
    // Import these renderers when needed for read-only location fields
    const { locationRenderer } = require("../views/leads/leadsGrid/leads-renderers");
    cellRenderer = form.read_only ? locationRenderer : null; // Use editable if not read-only
  } else if (form.type === "date") {
    const { dateRenderer } = require("../views/leads/leadsGrid/leads-renderers");
    cellRenderer = form.read_only ? dateRenderer : null; // Use editable if not read-only
  }
  
  // If no specific renderer and not read-only, use the editable renderer
  if (!cellRenderer && !form.read_only) {
    logger.log('🔧 Assigning editableCustomFieldRenderer to field:', form.value, form.label);
    const { editableCustomFieldRenderer } = require("../views/leads/leadsGrid/leads-renderers");
    cellRenderer = editableCustomFieldRenderer;
  }
  
  // For read-only fields without specific renderers, use default display
  if (!cellRenderer && form.read_only) {
    // Use a simple display renderer - we'll import this at runtime
    cellRenderer = (params: any) => {
      const { value } = params;
      if (!value && value !== 0) return React.createElement('span', {}, '-');
      
      return React.createElement(
        'div',
        { className: 'h-100 align-items-center d-flex' },
        React.createElement(
          'div',
          { className: 'lead_cell' },
          React.createElement(
            'div',
            { className: 'cell_label', title: value },
            value
          )
        )
      );
    };
  }
  
  // Determine the field path
  let field = form.value;
  
  // For nested fields
  if (form.value !== "saleValue" && form.value !== "email" && form.value !== "notes") {
    field = `extraDetails.${form.value}`;
  }
  
  logger.log('🔧 Created column definition for:', form.value, 'with renderer:', cellRenderer?.name || 'anonymous function');
  
  return {
    headerName: form.label,
    sortable: false,
    width: columnWidths[form.value] || 150,
    field: field,
    colId: form.value,
    cellRenderer,
  };
};